import { SimpleSEO } from '@/components/seo/SimpleSEO';
import { Link } from 'react-router-dom';
import { Rocket, Shield, Zap, Users, Code, Globe } from 'lucide-react';

export function About() {
  return (
    <>
      <SimpleSEO
        title="About MdbApp | Leading Token Creation Platform"
        description="Learn about MdbApp - The premier platform for creating, launching, and managing verified ERC20/BEP20 tokens on Ethereum, BSC, and Avalanche networks"
        keywords="about MdbApp, token creator, crypto platform, blockchain services"
      />
      
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <h1 className="text-4xl font-bold mb-8 text-center pb-2 [background-image:linear-gradient(to_right,#0a74ff,#FFD700,#0a74ff)] [-webkit-background-clip:text] [-webkit-text-fill-color:transparent] [background-size:200%_auto] animate-gradient">
          About MdbApp
        </h1>

        <div className="space-y-8 text-foreground">
          <section>
            <h2 className="text-2xl font-semibold mb-4">Our Mission</h2>
            <p className="mb-4 text-lg">
              MdbApp is dedicated to democratizing blockchain technology by making token creation and management accessible to everyone. We believe that powerful crypto tools shouldn't require coding expertise or technical knowledge.
            </p>
            <p className="text-lg">
              Our mission is to empower entrepreneurs, developers, and crypto enthusiasts to bring their blockchain projects to life with professional-grade tools that are simple, secure, and reliable.
            </p>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-6">What We Do</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 rounded-xl border border-[#0A74FF]/20 bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-[#0A74FF]/10">
                    <Rocket className="w-6 h-6 text-[#0A74FF]" />
                  </div>
                  <h3 className="text-xl font-semibold">Token Creation</h3>
                </div>
                <p className="text-muted-foreground">
                  Create custom ERC20/BEP20 tokens with advanced features like taxes, reflection rewards, auto-liquidity, and burn mechanisms - all without writing a single line of code.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-green-500/20 bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <Shield className="w-6 h-6 text-green-500" />
                  </div>
                  <h3 className="text-xl font-semibold">Security Tools</h3>
                </div>
                <p className="text-muted-foreground">
                  Advanced contract scanning, security analysis, and locking features to protect your project and build trust with your community.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-purple-500/20 bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-purple-500/10">
                    <Zap className="w-6 h-6 text-purple-500" />
                  </div>
                  <h3 className="text-xl font-semibold">Management Tools</h3>
                </div>
                <p className="text-muted-foreground">
                  Comprehensive token management interface to adjust taxes, limits, burn tokens, and control all aspects of your token after deployment.
                </p>
              </div>

              <div className="p-6 rounded-xl border border-yellow-500/20 bg-card">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Globe className="w-6 h-6 text-yellow-500" />
                  </div>
                  <h3 className="text-xl font-semibold">Multi-Chain Support</h3>
                </div>
                <p className="text-muted-foreground">
                  Deploy tokens across Ethereum, BNB Smart Chain, and Avalanche networks with the same easy-to-use interface.
                </p>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Why Choose MdbApp?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-[#0A74FF]/10 mt-1">
                  <Code className="w-5 h-5 text-[#0A74FF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">No Coding Required</h3>
                  <p className="text-muted-foreground">
                    Our intuitive interface makes token creation accessible to everyone, regardless of technical background. Simply configure your settings and deploy.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-green-500/10 mt-1">
                  <Shield className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Automatic Verification</h3>
                  <p className="text-muted-foreground">
                    All tokens created through MdbApp are automatically verified on blockchain explorers (Etherscan, BscScan, SnowTrace), building instant credibility.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-purple-500/10 mt-1">
                  <Zap className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Instant Deployment</h3>
                  <p className="text-muted-foreground">
                    Deploy your token in minutes, not days. Our streamlined process gets your project live quickly so you can focus on building your community.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="p-2 rounded-lg bg-yellow-500/10 mt-1">
                  <Users className="w-5 h-5 text-yellow-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold mb-2">Community Trusted</h3>
                  <p className="text-muted-foreground">
                    Thousands of successful token projects have been launched using MdbApp. Join a thriving community of creators and innovators.
                  </p>
                </div>
              </div>
            </div>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Our Technology</h2>
            <p className="mb-4">
              MdbApp is built on cutting-edge blockchain technology with a focus on security, reliability, and user experience:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Smart contracts audited and optimized for gas efficiency</li>
              <li>Integration with industry-leading wallet providers (MetaMask, WalletConnect)</li>
              <li>Real-time blockchain data and transaction monitoring</li>
              <li>Advanced contract analysis and security scanning</li>
              <li>Mobile-first design for on-the-go management</li>
              <li>Continuous updates and feature improvements</li>
            </ul>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Our Commitment</h2>
            <p className="mb-4">
              We are committed to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li><strong>Security:</strong> Protecting your assets and data with industry-best practices</li>
              <li><strong>Transparency:</strong> Open and honest communication about our services and fees</li>
              <li><strong>Innovation:</strong> Continuously improving and adding new features</li>
              <li><strong>Support:</strong> Providing resources and guidance to help you succeed</li>
              <li><strong>Accessibility:</strong> Making blockchain technology available to everyone</li>
            </ul>
          </section>

          <section className="border-t pt-8">
            <h2 className="text-2xl font-semibold mb-4">Get Started Today</h2>
            <p className="mb-6">
              Ready to launch your token project? MdbApp makes it easy to create, deploy, and manage professional cryptocurrency tokens across multiple blockchain networks.
            </p>
            <div className="flex gap-4 justify-center">
              <Link 
                to="/create" 
                className="inline-flex items-center px-6 py-3 rounded-lg bg-[#0A74FF] text-white hover:bg-[#0A74FF]/80 transition-colors font-semibold"
              >
                Create Your Token
              </Link>
              <Link 
                to="/blog" 
                className="inline-flex items-center px-6 py-3 rounded-lg border border-[#0A74FF] text-[#0A74FF] hover:bg-[#0A74FF]/10 transition-colors font-semibold"
              >
                Read Our Guides
              </Link>
            </div>
          </section>

          <section className="border-t pt-8 text-center">
            <p className="text-muted-foreground">
              Have questions? <Link to="/contact" className="text-[#0A74FF] hover:underline">Contact us</Link> or check out our <Link to="/blog" className="text-[#0A74FF] hover:underline">guides and tutorials</Link>.
            </p>
          </section>
        </div>
      </div>
    </>
  );
}
